// src/pages/Dashboard.jsx
import { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gem, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import AddContactDrawer from '../components/AddContactDrawer';
import AddPropertyDrawer from '../components/AddPropertyDrawer';
import CheckoutDrawer from '../components/CheckoutDrawer';
import { AuthContext } from '../context/AuthContext';
import { getProperties } from '../api/properties';
import { getDashboardOverview, markDashboardNotificationRead } from '../api/dashboard';
import { listExpenses } from '../api/expenses';
import { supabase } from '../supabaseClient';
import Chart from 'chart.js/auto';
import axiosInstance from '../api/axiosInstance';
import { getNotificationTargetUrl } from '../lib/notificationTargets';

// Helper: derive a first name from whichever field is present
function deriveFirstName(user) {
  if (!user) return '';
  const raw =
    user.first_name ??
    user.firstName ??
    user.name ??
    user.fullName ??
    '';
  const str = String(raw).trim();
  if (!str) return '';
  return str.split(/\s+/)[0];
}

function formatCurrency(value) {
  return `£${Math.round(Number(value) || 0).toLocaleString('en-GB')}`;
}

function Dashboard() {
  const { user, isPlusActive, isEnhancedActive } = useContext(AuthContext);
  const navigate = useNavigate();
  const userName = deriveFirstName(user) || 'there';

  const [drawerType, setDrawerType] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false); // ✅ Separate state for checkout drawer

  const [properties, setProperties] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardOverview, setDashboardOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // ✅ Real financial data (replaces hardcoded)
  const [financialLoading, setFinancialLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [financialSeries, setFinancialSeries] = useState({
    labels: [],
    income: [],
    expenses: [],
    totals: null,
  });

  // ✅ Contacts for occupancy
  const [contacts, setContacts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  // Chart refs
  const netGainChartRef = useRef(null);
  const occupancyChartRef = useRef(null);

  // Chart instances
  const netGainInstance = useRef(null);
  const occupancyInstance = useRef(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const closeDrawer = () => setDrawerType(null);

  const fetchDashboardOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await getDashboardOverview();
      setDashboardOverview(res?.data || null);
    } catch (err) {
      console.error('[Dashboard] Failed to load overview:', err);
      setDashboardOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProperties();
      const raw = res?.data ?? [];

      const propertiesWithImageUrls = raw.map((property) => {
        let imageUrl = property.imageUrl || null;
        if (!imageUrl && property.image) {
          try {
            const { data, error } = supabase.storage
              .from('property-images')
              .getPublicUrl(property.image);
            imageUrl = error ? null : data?.publicUrl ?? null;
          } catch {
            imageUrl = null;
          }
        }
        return { ...property, imageUrl };
      });

      setProperties(propertiesWithImageUrls);

      // Sort recent properties
      const parseDate = (p) => {
        const d = p?.created_at ?? p?.createdAt ?? p?.created ?? null;
        const parsed = d ? Date.parse(d) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
      };

      const sorted = [...propertiesWithImageUrls].sort((a, b) => {
        const da = parseDate(a);
        const db = parseDate(b);
        if (da !== null && db !== null) return db - da;
        if (da === null && db !== null) return 1;
        if (db === null && da !== null) return -1;
        return 0;
      });

      setRecentProperties(sorted);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      setProperties([]);
      setRecentProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Fetch contacts so occupancy can be derived from Tenant + propertyId
  const fetchContacts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/contacts', {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      });

      const data = res?.data;
      const list = data?.contacts || data?.data?.contacts || data || [];
      setContacts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[Dashboard] Failed to load contacts:', err);
      setContacts([]);
    }
  }, []);

  // ✅ Fetch real payments + expenses (force backend domain via axiosInstance)
  const fetchFinancials = useCallback(async () => {
    setFinancialLoading(true);
    try {
      const getAmount = (...values) => {
        for (const value of values) {
          if (value === null || value === undefined || value === '') continue;
          const amount = Number(value);
          if (Number.isFinite(amount)) return amount;
        }
        return 0;
      };

      if (!isPlusActive) {
        const expensesRes = await listExpenses();
        const rawExpenses =
          expensesRes?.data?.expenses ||
          expensesRes?.data?.data?.expenses ||
          expensesRes?.expenses ||
          expensesRes?.data ||
          [];

        const safeExpenses = (Array.isArray(rawExpenses) ? rawExpenses : []).map((expense) => ({
          ...expense,
          amount: getAmount(expense.amount, expense.amountGBP, expense.cost),
          date:
            expense.date ||
            expense.createdAt ||
            expense.created_at ||
            expense.incurredOn ||
            expense.incurred_on,
        }));

        setPayments([]);
        setExpenses(safeExpenses);
        setFinancialSeries({ labels: [], income: [], expenses: [], totals: null });
        return;
      }

      const [paymentsResult, expensesResult, financialsResult] = await Promise.allSettled([
        axiosInstance.get('/api/payments', { withCredentials: true }),
        listExpenses(),
        axiosInstance.get('/api/analytics/financials', { withCredentials: true }),
      ]);

      if (paymentsResult.status === 'rejected') {
        console.error('[Dashboard] Failed to load payments:', paymentsResult.reason);
      }

      if (expensesResult.status === 'rejected') {
        console.error('[Dashboard] Failed to load expenses:', expensesResult.reason);
      }

      if (financialsResult.status === 'rejected') {
        console.error('[Dashboard] Failed to load financial chart data:', financialsResult.reason);
      }

      const paymentsRes =
        paymentsResult.status === 'fulfilled'
          ? paymentsResult.value
          : { data: [] };

      const expensesRes =
        expensesResult.status === 'fulfilled'
          ? expensesResult.value
          : { expenses: [] };

      const financialsRes =
        financialsResult.status === 'fulfilled'
          ? financialsResult.value
          : { data: {} };

      const rawPayments =
        paymentsRes?.data?.payments ||
        paymentsRes?.data?.data?.payments ||
        paymentsRes?.data ||
        [];

      const rawExpenses =
        expensesRes?.data?.expenses ||
        expensesRes?.data?.data?.expenses ||
        expensesRes?.expenses ||
        expensesRes?.data ||
        [];

      const rawFinancials = financialsRes?.data || financialsRes || {};

      const safePayments = (Array.isArray(rawPayments) ? rawPayments : []).map((payment) => ({
        ...payment,
        amount: getAmount(
          payment.amount,
          payment.amountGBP,
          payment.rentAmountGBP,
          payment.rentAmount,
          payment.total
        ),
      }));

      const safeExpenses = (Array.isArray(rawExpenses) ? rawExpenses : []).map((expense) => ({
        ...expense,
        amount: getAmount(expense.amount, expense.amountGBP, expense.cost),
        date:
          expense.date ||
          expense.createdAt ||
          expense.created_at ||
          expense.incurredOn ||
          expense.incurred_on,
      }));

      setPayments(safePayments);
      setExpenses(safeExpenses);
      setFinancialSeries({
        labels: Array.isArray(rawFinancials.labels) ? rawFinancials.labels : [],
        income: Array.isArray(rawFinancials.income) ? rawFinancials.income : [],
        expenses: Array.isArray(rawFinancials.expenses) ? rawFinancials.expenses : [],
        totals: rawFinancials.totals || null,
      });
    } catch (err) {
      console.error('[Dashboard] Failed to load payments/expenses:', err);
      setPayments([]);
      setExpenses([]);
      setFinancialSeries({ labels: [], income: [], expenses: [], totals: null });
    } finally {
      setFinancialLoading(false);
    }
  }, [isPlusActive]);

  const fetchNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const res = await axiosInstance.get('/api/dashboard/notifications', {
        withCredentials: true,
      });

      setNotifications(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('[Dashboard] Failed to load notifications:', err);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const markNotificationRead = useCallback(async (notificationId) => {
    setNotifications((current) =>
      current.map((notification) =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      )
    );

    try {
      await markDashboardNotificationRead(notificationId);
    } catch (err) {
      console.error('[Dashboard] Failed to mark notification read:', err);
    }
  }, []);

  const handleDashboardNotificationClick = useCallback(async (notification) => {
    const targetUrl = getNotificationTargetUrl(notification);
    if (!targetUrl) return;
    await markNotificationRead(notification.id);
    navigate(targetUrl);
  }, [markNotificationRead, navigate]);

  useEffect(() => {
    fetchDashboardOverview();
  }, [fetchDashboardOverview]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchFinancials();
  }, [fetchFinancials]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ✅ Summary numbers (real)
  const totalIncome = useMemo(() => {
    const aggregateIncome = Number(financialSeries.totals?.income);
    if (Number.isFinite(aggregateIncome)) return aggregateIncome;
    return payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }, [financialSeries.totals, payments]);

  const totalExpenses = useMemo(() => {
    const aggregateExpenses = Number(financialSeries.totals?.expenses);
    if (Number.isFinite(aggregateExpenses)) return aggregateExpenses;
    return expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [expenses, financialSeries.totals]);

  const netProfit = useMemo(() => {
    const aggregateNetProfit = Number(financialSeries.totals?.netProfit);
    if (Number.isFinite(aggregateNetProfit)) return aggregateNetProfit;
    return totalIncome - totalExpenses;
  }, [financialSeries.totals, totalIncome, totalExpenses]);

  // ✅ Chart data: last 4 months net profit (income-expenses)
  const dashboardMonthlyIncome = useMemo(() => {
    const value =
      dashboardOverview?.monthlyIncome ??
      dashboardOverview?.finance?.monthlyIncome ??
      null;

    return value === null || value === undefined ? null : Number(value) || 0;
  }, [dashboardOverview]);

  const { netLabels, incomeValues, expenseValues } = useMemo(() => {
    const hasFinancialSeries =
      financialSeries.labels.length > 0 &&
      (financialSeries.income.length > 0 || financialSeries.expenses.length > 0);

    if (hasFinancialSeries) {
      const startIndex = Math.max(financialSeries.labels.length - 4, 0);
      const labels = financialSeries.labels.slice(startIndex);
      const income = labels.map((_, index) => {
        const sourceIndex = startIndex + index;
        return Number(financialSeries.income[sourceIndex]) || 0;
      });
      const expense = labels.map((_, index) => {
        const sourceIndex = startIndex + index;
        return Number(financialSeries.expenses[sourceIndex]) || 0;
      });

      return { netLabels: labels, incomeValues: income, expenseValues: expense };
    }

    const now = new Date();

    // build last 4 month keys inclusive
    const months = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('en-GB', { month: 'short' });
      months.push({ key, label, year: d.getFullYear(), monthIndex: d.getMonth() });
    }

    const incomeByMonth = {};
    const expenseByMonth = {};

    for (const m of months) {
      incomeByMonth[m.key] = 0;
      expenseByMonth[m.key] = 0;
    }

    const parseAnyDate = (val) => {
      if (!val) return null;
      const t = Date.parse(val);
      return Number.isFinite(t) ? new Date(t) : null;
    };

    // Payments: use lastPaid if present, else dueDate, else ignore
    for (const p of payments) {
      const d =
        parseAnyDate(p.lastPaid) ||
        parseAnyDate(p.paidAt) ||
        parseAnyDate(p.paid_at) ||
        parseAnyDate(p.dueDate) ||
        parseAnyDate(p.due_date);

      if (!d) continue;

      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (incomeByMonth[key] === undefined) continue;

      incomeByMonth[key] += Number(p.amount) || 0;
    }

    // Expenses: use date/createdAt/etc
    for (const e of expenses) {
      const d =
        parseAnyDate(e.date) ||
        parseAnyDate(e.createdAt) ||
        parseAnyDate(e.created_at) ||
        parseAnyDate(e.incurredOn) ||
        parseAnyDate(e.incurred_on);

      if (!d) continue;

      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (expenseByMonth[key] === undefined) continue;

      expenseByMonth[key] += Number(e.amount) || 0;
    }

    const labels = months.map((m) => m.label);
    const income = months.map((m) => {
      return incomeByMonth[m.key] || 0;
    });
    const expense = months.map((m) => {
      return expenseByMonth[m.key] || 0;
    });

    return { netLabels: labels, incomeValues: income, expenseValues: expense };
  }, [payments, expenses, financialSeries]);

  // ✅ Occupancy fix:
  // - If backend provides `occupied` boolean, use it
  // - Otherwise fallback to existing logic (status/contacts)
  const { occupiedCount, vacantCount } = useMemo(() => {
    const total = Array.isArray(properties) ? properties.length : 0;
    if (!total) return { occupiedCount: 0, vacantCount: 0 };

    const tenantPropertyIds = new Set(
      (contacts || [])
        .filter((c) => (c?.relationship || '').toString().toLowerCase() === 'tenant')
        .map((c) => (c?.propertyId || c?.property || '').toString())
        .filter(Boolean)
    );

    const occupied = properties.filter((p) => {
      if (typeof p?.occupied === 'boolean') return p.occupied;

      const status = (p?.status || '').toString().toLowerCase();
      if (status === 'occupied') return true;
      if (tenantPropertyIds.has((p?.id || '').toString())) return true;
      return false;
    }).length;

    const vacant = Math.max(total - occupied, 0);
    return { occupiedCount: occupied, vacantCount: vacant };
  }, [properties, contacts]);

  const whatsNewNotifications = useMemo(() => {
    return [...notifications]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 3);
  }, [notifications]);

  const dashboardStats = useMemo(() => {
    const tenantCountFromContacts = contacts.filter(
      (contact) => (contact?.relationship || '').toString().toLowerCase() === 'tenant'
    ).length;

    return [
      {
        label: 'Total Properties',
        value:
          overviewLoading && dashboardOverview === null
            ? '—'
            : (dashboardOverview?.totalProperties ?? properties.length).toLocaleString('en-GB'),
      },
      {
        label: 'Total Tenants',
        value:
          overviewLoading && dashboardOverview === null
            ? '—'
            : (dashboardOverview?.totalTenants ?? tenantCountFromContacts).toLocaleString('en-GB'),
      },
      {
        label: 'Monthly Income',
        value:
          overviewLoading && dashboardOverview === null
            ? '—'
            : formatCurrency(dashboardMonthlyIncome ?? 0),
      },
    ];
  }, [contacts, dashboardMonthlyIncome, dashboardOverview, overviewLoading, properties.length]);

  // Chart setup (✅ now uses real data)
  useEffect(() => {
    // Income / Expenses Bar
    if (netGainChartRef.current) {
      if (netGainInstance.current) netGainInstance.current.destroy();
      const ctx = netGainChartRef.current.getContext('2d');
      netGainInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: netLabels,
          datasets: [
            {
              label: 'Income',
              data: incomeValues,
              backgroundColor: 'rgba(75,192,192,0.8)',
              borderRadius: 2,
              barPercentage: 0.72,
              categoryPercentage: 0.7,
            },
            {
              label: 'Expenses',
              data: expenseValues,
              backgroundColor: 'rgba(255, 99, 132, 0.7)',
              borderRadius: 2,
              barPercentage: 0.72,
              categoryPercentage: 0.7,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              align: 'center',
              labels: { color: '#fff', font: { size: 12 } },
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = Math.abs(Number(context.raw) || 0);
                  return `${context.dataset.label}: £${value.toLocaleString('en-GB')}`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                color: '#fff',
                font: { size: 11 },
                callback: (value) => `£${Number(value).toLocaleString('en-GB')}`,
              },
              grid: { color: 'rgba(255,255,255,0.1)' },
            },
            x: {
              ticks: { color: '#fff', font: { size: 11 } },
              grid: { color: 'rgba(255,255,255,0.1)' },
            },
          },
        },
      });
    }

    // Occupancy Doughnut
    if (occupancyChartRef.current) {
      if (occupancyInstance.current) occupancyInstance.current.destroy();
      const ctx = occupancyChartRef.current.getContext('2d');
      occupancyInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Occupied', 'Vacant'],
          datasets: [
            {
              data: [occupiedCount, vacantCount],
              backgroundColor: ['rgba(75,192,192,0.8)', 'rgba(200,200,200,0.3)'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { color: '#fff', font: { size: 12 } },
            },
          },
          cutout: '70%',
        },
      });
    }

    return () => {
      if (netGainInstance.current) netGainInstance.current.destroy();
      if (occupancyInstance.current) occupancyInstance.current.destroy();
    };
  }, [netLabels, incomeValues, expenseValues, occupiedCount, vacantCount]);

  const handlePropertyCreated = useCallback(async () => {
    try {
      await Promise.all([fetchProperties(), fetchDashboardOverview()]);
      setDrawerType(null);
    } catch (err) {
      console.error('[Dashboard] error refetching after create', err);
    }
  }, [fetchDashboardOverview, fetchProperties]);

  const planBadgeLabel = isEnhancedActive
    ? 'Beem Enhanced'
    : isPlusActive
    ? 'Beem Plus'
    : 'UPGRADE TO BEEM PLUS';

  return (
    <div className="pt-10 md:pt-0">
      {/* Greeting */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl text-[#ffffff]">
          {getGreeting()}, {userName}
        </h1>

        <button
          type="button"
          onClick={isPlusActive ? undefined : () => navigate('/settings')}
          disabled={isPlusActive}
          className={[
            'hidden md:flex items-center gap-2 px-4 py-2 border rounded-xl backdrop-blur-md',
            'text-white',
            isPlusActive
              ? 'bg-[#AF9B8A]/80 cursor-default'
              : 'bg-white/10 border-white/10 hover:bg-[#AF9B8A]/75 transition',
          ].join(' ')}
        >
          <Gem className="w-5 h-5" />
          <span>{planBadgeLabel}</span>
        </button>
      </div>

      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-10">
        <div className="p-6 bg-white/10 border border-white/10 rounded-xl backdrop-blur-md">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-xl font-semibold">What's new?</h3>
          </div>

          <div className="space-y-3">
            {notificationsLoading ? (
              <div className="text-sm text-gray-200">Loading updates...</div>
            ) : whatsNewNotifications.length > 0 ? (
              whatsNewNotifications.map((notification) => {
                const targetUrl = getNotificationTargetUrl(notification);
                const content = (
                  <>
                    <span className="font-semibold text-white">{notification.title}</span>
                    {notification.message ? ` - ${notification.message}` : ''}
                  </>
                );

                return targetUrl ? (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleDashboardNotificationClick(notification)}
                    className="block w-full rounded-md p-1 text-left text-sm text-gray-200 hover:bg-[#AF9B8A]/15 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                  >
                    {content}
                  </button>
                ) : (
                  <div key={notification.id} className="text-sm text-gray-200">
                    {content}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-200">No new notifications.</div>
            )}
          </div>
        </div>

        <div className="xl:col-span-3 grid grid-cols-1 sm:grid-cols-3 bg-white/10 border border-white/10 rounded-xl backdrop-blur-md overflow-hidden">
          {dashboardStats.map((stat, index) => (
            <div
              key={stat.label}
              className={[
                'p-6 min-h-32 flex flex-col justify-center',
                index > 0 ? 'border-t sm:border-t-0 sm:border-l border-white/10' : '',
              ].join(' ')}
            >
              <h3 className="text-sm font-semibold text-gray-300 mb-4">{stat.label}</h3>
              <p className="text-5xl font-thin text-[#AF9B8A]">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Action Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div
          onClick={() => setDrawerType('property')}
          className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md hover:bg-[#AF9B8A]/10 hover:cursor-pointer"
        >
          <h3 className="text-xl font-semibold mb-2">Add a Property</h3>
          <p className="text-sm text-gray-300">Add your new rental property here.</p>
        </div>

        <div
          onClick={() => setDrawerType('contact')}
          className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md hover:bg-[#AF9B8A]/10 hover:cursor-pointer"
        >
          <h3 className="text-xl font-semibold mb-2">Add a Contact</h3>
          <p className="text-sm text-gray-300">Add a new contact here.</p>
        </div>

        <Link to="/tasks">
          <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md hover:bg-[#AF9B8A]/10">
            <h3 className="text-xl font-semibold mb-2">Tasks & Jobs</h3>
            <p className="text-sm text-gray-300">Stay on top of landlord admin and maintenance work.</p>
          </div>
        </Link>
      </div>

      {/* Portfolio Summary */}
      <h2 className="text-3xl text-[#ffffff] mb-6 pt-16">Portfolio summary</h2>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md">
          <h3 className="text-xl font-semibold mb-4 text-left">Net Gain / Net Loss</h3>
          <div className="w-full h-64">
            <canvas ref={netGainChartRef}></canvas>
          </div>
        </div>
        <div className="p-6 bg-white/10 rounded-xl border border-white/10 backdrop-blur-md flex flex-col items-center">
          <h3 className="text-xl font-semibold mb-4 text-left w-full">Tenancy Occupation</h3>
          <div className="w-60 h-60">
            <canvas ref={occupancyChartRef}></canvas>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="p-6 bg-white/10 border border-white/10 rounded-xl backdrop-blur-md flex items-center justify-between">
          <div>
            <h3 className="text-gray-300 text-sm">Total Income</h3>
            <p className="text-2xl font-semibold mt-1">
              {financialLoading ? '—' : `£${totalIncome.toLocaleString()}`}
            </p>
          </div>
          <TrendingUp className="w-8 h-8 text-[#AF9B8A]" />
        </div>

        <div className="p-6 bg-white/10 border border-white/10 rounded-xl backdrop-blur-md flex items-center justify-between">
          <div>
            <h3 className="text-gray-300 text-sm">Total Expenses</h3>
            <p className="text-2xl font-semibold mt-1">
              {financialLoading ? '—' : `£${totalExpenses.toLocaleString()}`}
            </p>
          </div>
          <TrendingDown className="w-8 h-8 text-red-400" />
        </div>

        <div className="p-6 bg-white/10 border border-white/10 rounded-xl backdrop-blur-md flex items-center justify-between">
          <div>
            <h3 className="text-gray-300 text-sm">Net Profit</h3>
            <p className="text-2xl font-semibold mt-1">
              {financialLoading ? '—' : `£${netProfit.toLocaleString()}`}
            </p>
          </div>
          <Wallet className="w-8 h-8 text-green-400" />
        </div>
      </div>

      {/* Recent Properties */}
      <h2 className="text-3xl text-[#ffffff] mb-6">Recent properties</h2>
      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      {loading ? (
        <div className="text-white opacity-70 text-center py-12">
          Loading recent properties...
        </div>
      ) : recentProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {recentProperties.map((property) => (
            <PropertyCard
              key={property.id}
              property={{
                image:
                  property.imageUrl ??
                  property.image ??
                  'https://images.unsplash.com/photo-1621983209344-d3152696e84f?q=80&w=2940&auto=format&fit=crop',
                title: property.title || property.name || 'Untitled Property',
                description: property.description || property.address || '',
                ...property,
              }}
            />
          ))}
        </div>
      ) : (
        <div className="text-white opacity-60 py-12">No properties found.</div>
      )}

      {/* Drawer Overlay */}
      {drawerType && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={closeDrawer}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
          <div
            className="relative w-full max-w-xl h-full bg-[#1e1e1e] border-l border-white/10 transform transition-transform duration-300 ease-out translate-x-0"
            onClick={(e) => e.stopPropagation()}
          >
            {drawerType === 'contact' && (
              <AddContactDrawer
                onSave={async () => {
                  closeDrawer();
                  await Promise.all([fetchContacts(), fetchProperties(), fetchDashboardOverview()]);
                }}
                onClose={closeDrawer}
                properties={properties}
              />
            )}
            {drawerType === 'property' && (
              <AddPropertyDrawer
                isOpen={true}
                onCreate={handlePropertyCreated}
                onSave={handlePropertyCreated}
                onClose={closeDrawer}
              />
            )}
          </div>
        </div>
      )}

      {/* ✅ Checkout Drawer */}
      <CheckoutDrawer open={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} />
    </div>
  );
}

export default Dashboard;
